import { AccessDeniedPage } from "@climatepartner/components/ErrorPages";
import { ReactElement, useContext, useMemo } from "react";
import { Navigate } from "react-router-dom";

import { UserContext } from "../../../shared/auth/user-context";
import { getAccessTokenPermissions, isUserElevated } from "../../../shared/auth/user-utils";
import { NETWORK_PATH } from "../../../shared/routing/paths/network";
import { useNetworkCapabilities } from "../public/use-network-capabilities";

/**
 * Elevated-employee check for redirect-destination decisions only, not an access gate.
 * Reads the access token's `permissions` claim rather than `hasPermission` (Frontegg
 * entitlements): in a tenant without a configured plan, entitlements deny everything —
 * including the employee permissions themselves — while the token still identifies the
 * employee reliably (CFC-2591).
 */
export function useIsElevatedFromToken(): boolean {
  const { user } = useContext(UserContext);
  const accessToken = user?.accessToken;
  const permissions = useMemo(() => getAccessTokenPermissions(accessToken), [accessToken]);

  return isUserElevated((permission) => permissions.includes(permission));
}

/**
 * For a route guard denying a specific side, or a detail deep link that 404'd (e.g. after an account
 * switch). An account with any network capability is redirected to the `/network` landing instead of
 * dead-ending on a 404 (CFC-2591). An account with none sees the 403 page in place, like every other
 * permission-gated area, rather than a silent redirect that hides the missing permission.
 */
export function NetworkNotFoundRedirect(): ReactElement {
  const { isBuyer, isSupplier } = useNetworkCapabilities();

  if (isBuyer || isSupplier) {
    return <Navigate to={NETWORK_PATH} replace />;
  }

  return <AccessDeniedPage />;
}
